<script setup>
import { toRef } from 'vue'
import { setup } from './useBasicForm'
import { getMaster } from '../../../Master.vue'
import { useErrorMessage } from '@/studentsite/common/useErrorMessage'
import { RouterLink } from 'vue-router'
import { useComponentUniqueId } from '@/utils/composables/useComponentUniqueId'
/**
 * @import { PropType } from 'vue'
 * @import { Props, Emits } from './emitTypes'
 */

const compId = useComponentUniqueId()

/**
 * @type Emits
 */
const emits = defineEmits(['setParam'])

const props = defineProps({
  inputErrors: {
    /** @type {PropType<Props["inputErrors"]>} */
    type: Object,
    required: true,
  },
  formParams: {
    /** @type {PropType<Props["formParams"]>} */
    type: Object,
    required: true,
  },
})

const {
  formParams,
  listTelValidation,
  listIdValidation,
  listPwValidation,
  listFirstNameValidation,
  listLastNameValidation,
  submitable,
  next,
  checkTel,
  checkLoginId,
  checkLoginPw,
  checkLastName,
  checkFirstName,
} = setup(props, emits)

const { has: errorHas, get: getError } = useErrorMessage(toRef(() => props.inputErrors).value)
const genderMaster = getMaster('gender_master') ?? {}
</script>

<template>
  <div class="container container--signup">
    <div class="navWrap">
      <div class="navArea">
        <ul class="stepBar stepBar--3step">
          <li class="step active">基本情報</li>
          <li class="step">大学情報</li>
          <li class="step">登録完了</li>
        </ul>
      </div>
    </div>
    <div class="contentWrap">
      <div class="formArea">
        <div class="formSet">
          <div class="title">
            名前
            <div class="md_label md_label--must">必須</div>
          </div>
          <div class="form column">
            <div class="columnWrap">
              <input
                type="text"
                name="last_name"
                placeholder="姓"
                class="form--line form--w100"
                :class="{ md_error: errorHas('last_name') || listLastNameValidation.length }"
                v-model="formParams.last_name"
                @change="checkLastName"
              />
              <p
                class="errorMessage"
                v-if="listLastNameValidation.length"
              >
                <span class="iconImgPart">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 150 1100 1100"
                  >
                    <path
                      d="m993.554 865.69-.267-.267-364.968-609.718c0-.267-.267-.267-.267-.544-23.986-38.818-65.23-61.994-110.515-61.994s-86.524 23.182-110.246 61.727c-.267.267-.544.544-.544 1.078L41.509 865.69s0 .267-.267.267c-11.321 19.409-17.52 42.051-17.52 64.96 0 70.622 57.416 128.574 127.767 129.382h731.833c70.622-.811 128.036-58.76 128.036-129.382 0-22.909-6.199-45.551-17.787-65.23zM882.5 974.05H152.564c-23.453-.267-42.318-19.676-42.318-43.129 0-7.544 1.889-14.821 5.66-21.298l364.968-609.448.267-.267c8.088-12.665 21.564-20.22 36.661-20.22s28.841 7.821 36.929 20.753L919.159 909.35c3.777 6.466 5.66 14.021 5.66 21.564.267 23.453-18.865 42.86-42.318 43.129zM517.53 456.516c-23.72 0-43.129 19.409-43.129 43.129v172.51c0 23.72 19.409 43.129 43.129 43.129s43.129-19.409 43.129-43.129v-172.51c0-23.72-19.409-43.129-43.129-43.129zm0 345.023c-11.321 0-22.375 4.577-30.461 12.665s-12.665 19.142-12.665 30.461 4.577 22.375 12.665 30.461c8.088 8.088 19.142 12.665 30.461 12.665s22.375-4.577 30.461-12.665c8.088-8.088 12.665-19.142 12.665-30.461s-4.577-22.375-12.665-30.461c-8.088-8.088-19.142-12.665-30.461-12.665z"
                      fill="#f04438"
                    ></path>
                  </svg> </span
                >{{ listLastNameValidation[0] }}
              </p>
              <p
                class="errorMessage"
                v-else-if="errorHas('last_name')"
              >
                <span class="iconImgPart">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 150 1100 1100"
                  >
                    <path
                      d="m993.554 865.69-.267-.267-364.968-609.718c0-.267-.267-.267-.267-.544-23.986-38.818-65.23-61.994-110.515-61.994s-86.524 23.182-110.246 61.727c-.267.267-.544.544-.544 1.078L41.509 865.69s0 .267-.267.267c-11.321 19.409-17.52 42.051-17.52 64.96 0 70.622 57.416 128.574 127.767 129.382h731.833c70.622-.811 128.036-58.76 128.036-129.382 0-22.909-6.199-45.551-17.787-65.23zM882.5 974.05H152.564c-23.453-.267-42.318-19.676-42.318-43.129 0-7.544 1.889-14.821 5.66-21.298l364.968-609.448.267-.267c8.088-12.665 21.564-20.22 36.661-20.22s28.841 7.821 36.929 20.753L919.159 909.35c3.777 6.466 5.66 14.021 5.66 21.564.267 23.453-18.865 42.86-42.318 43.129zM517.53 456.516c-23.72 0-43.129 19.409-43.129 43.129v172.51c0 23.72 19.409 43.129 43.129 43.129s43.129-19.409 43.129-43.129v-172.51c0-23.72-19.409-43.129-43.129-43.129zm0 345.023c-11.321 0-22.375 4.577-30.461 12.665s-12.665 19.142-12.665 30.461 4.577 22.375 12.665 30.461c8.088 8.088 19.142 12.665 30.461 12.665s22.375-4.577 30.461-12.665c8.088-8.088 12.665-19.142 12.665-30.461s-4.577-22.375-12.665-30.461c-8.088-8.088-19.142-12.665-30.461-12.665z"
                      fill="#f04438"
                    ></path>
                  </svg> </span
                >{{ getError('last_name') }}
              </p>
            </div>
            <div class="columnWrap">
              <input
                type="text"
                name="first_name"
                placeholder="名"
                class="form--line form--w100"
                :class="{ md_error: errorHas('first_name') || listFirstNameValidation.length }"
                v-model="formParams.first_name"
                @change="checkFirstName"
              />
              <p
                class="errorMessage"
                v-if="listFirstNameValidation.length"
              >
                <span class="iconImgPart">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 150 1100 1100"
                  >
                    <path
                      d="m993.554 865.69-.267-.267-364.968-609.718c0-.267-.267-.267-.267-.544-23.986-38.818-65.23-61.994-110.515-61.994s-86.524 23.182-110.246 61.727c-.267.267-.544.544-.544 1.078L41.509 865.69s0 .267-.267.267c-11.321 19.409-17.52 42.051-17.52 64.96 0 70.622 57.416 128.574 127.767 129.382h731.833c70.622-.811 128.036-58.76 128.036-129.382 0-22.909-6.199-45.551-17.787-65.23zM882.5 974.05H152.564c-23.453-.267-42.318-19.676-42.318-43.129 0-7.544 1.889-14.821 5.66-21.298l364.968-609.448.267-.267c8.088-12.665 21.564-20.22 36.661-20.22s28.841 7.821 36.929 20.753L919.159 909.35c3.777 6.466 5.66 14.021 5.66 21.564.267 23.453-18.865 42.86-42.318 43.129zM517.53 456.516c-23.72 0-43.129 19.409-43.129 43.129v172.51c0 23.72 19.409 43.129 43.129 43.129s43.129-19.409 43.129-43.129v-172.51c0-23.72-19.409-43.129-43.129-43.129zm0 345.023c-11.321 0-22.375 4.577-30.461 12.665s-12.665 19.142-12.665 30.461 4.577 22.375 12.665 30.461c8.088 8.088 19.142 12.665 30.461 12.665s22.375-4.577 30.461-12.665c8.088-8.088 12.665-19.142 12.665-30.461s-4.577-22.375-12.665-30.461c-8.088-8.088-19.142-12.665-30.461-12.665z"
                      fill="#f04438"
                    ></path>
                  </svg> </span
                >{{ listFirstNameValidation[0] }}
              </p>
              <p
                class="errorMessage"
                v-else-if="errorHas('first_name')"
              >
                <span class="iconImgPart">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 150 1100 1100"
                  >
                    <path
                      d="m993.554 865.69-.267-.267-364.968-609.718c0-.267-.267-.267-.267-.544-23.986-38.818-65.23-61.994-110.515-61.994s-86.524 23.182-110.246 61.727c-.267.267-.544.544-.544 1.078L41.509 865.69s0 .267-.267.267c-11.321 19.409-17.52 42.051-17.52 64.96 0 70.622 57.416 128.574 127.767 129.382h731.833c70.622-.811 128.036-58.76 128.036-129.382 0-22.909-6.199-45.551-17.787-65.23zM882.5 974.05H152.564c-23.453-.267-42.318-19.676-42.318-43.129 0-7.544 1.889-14.821 5.66-21.298l364.968-609.448.267-.267c8.088-12.665 21.564-20.22 36.661-20.22s28.841 7.821 36.929 20.753L919.159 909.35c3.777 6.466 5.66 14.021 5.66 21.564.267 23.453-18.865 42.86-42.318 43.129zM517.53 456.516c-23.72 0-43.129 19.409-43.129 43.129v172.51c0 23.72 19.409 43.129 43.129 43.129s43.129-19.409 43.129-43.129v-172.51c0-23.72-19.409-43.129-43.129-43.129zm0 345.023c-11.321 0-22.375 4.577-30.461 12.665s-12.665 19.142-12.665 30.461 4.577 22.375 12.665 30.461c8.088 8.088 19.142 12.665 30.461 12.665s22.375-4.577 30.461-12.665c8.088-8.088 12.665-19.142 12.665-30.461s-4.577-22.375-12.665-30.461c-8.088-8.088-19.142-12.665-30.461-12.665z"
                      fill="#f04438"
                    ></path>
                  </svg> </span
                >{{ getError('first_name') }}
              </p>
            </div>
          </div>
        </div>
        <div class="formSet">
          <div class="title">
            性別
            <div class="md_label md_label--must">必須</div>
          </div>
          <div class="form">
            <ul class="toggleSet">
              <template
                v-for="(label, val) in genderMaster"
                :key="val"
              >
                <li class="md_toggle">
                  <input
                    type="radio"
                    :value="val"
                    name="gender"
                    :id="`radio_${val}_${compId}`"
                    :class="{ md_error: errorHas('gender') }"
                    v-model="formParams.gender"
                  /><label :for="`radio_${val}_${compId}`">{{ label }}</label>
                </li>
              </template>
            </ul>
          </div>
        </div>
        <div class="formSet">
          <div class="title">
            携帯番号（半角数字・ハイフンなし）
            <div class="md_label md_label--must">必須</div>
          </div>
          <div class="sub sub--min">スカウト等があった際にショートメッセージをお送りするために使用します</div>
          <div class="form">
            <input
              type="tel"
              name="tel"
              placeholder="テキストを入力"
              class="form--line form--w100"
              :class="{ md_error: errorHas('tel') || listTelValidation.length }"
              v-model="formParams.tel"
              @change="checkTel"
            />
            <p
              class="errorMessage"
              v-if="listTelValidation.length"
            >
              <span class="iconImgPart">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 150 1100 1100"
                >
                  <path
                    d="m993.554 865.69-.267-.267-364.968-609.718c0-.267-.267-.267-.267-.544-23.986-38.818-65.23-61.994-110.515-61.994s-86.524 23.182-110.246 61.727c-.267.267-.544.544-.544 1.078L41.509 865.69s0 .267-.267.267c-11.321 19.409-17.52 42.051-17.52 64.96 0 70.622 57.416 128.574 127.767 129.382h731.833c70.622-.811 128.036-58.76 128.036-129.382 0-22.909-6.199-45.551-17.787-65.23zM882.5 974.05H152.564c-23.453-.267-42.318-19.676-42.318-43.129 0-7.544 1.889-14.821 5.66-21.298l364.968-609.448.267-.267c8.088-12.665 21.564-20.22 36.661-20.22s28.841 7.821 36.929 20.753L919.159 909.35c3.777 6.466 5.66 14.021 5.66 21.564.267 23.453-18.865 42.86-42.318 43.129zM517.53 456.516c-23.72 0-43.129 19.409-43.129 43.129v172.51c0 23.72 19.409 43.129 43.129 43.129s43.129-19.409 43.129-43.129v-172.51c0-23.72-19.409-43.129-43.129-43.129zm0 345.023c-11.321 0-22.375 4.577-30.461 12.665s-12.665 19.142-12.665 30.461 4.577 22.375 12.665 30.461c8.088 8.088 19.142 12.665 30.461 12.665s22.375-4.577 30.461-12.665c8.088-8.088 12.665-19.142 12.665-30.461s-4.577-22.375-12.665-30.461c-8.088-8.088-19.142-12.665-30.461-12.665z"
                    fill="#f04438"
                  ></path>
                </svg> </span
              >{{ listTelValidation[0] }}
            </p>
            <p
              class="errorMessage"
              v-if="errorHas('tel')"
            >
              <span class="iconImgPart">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 150 1100 1100"
                >
                  <path
                    d="m993.554 865.69-.267-.267-364.968-609.718c0-.267-.267-.267-.267-.544-23.986-38.818-65.23-61.994-110.515-61.994s-86.524 23.182-110.246 61.727c-.267.267-.544.544-.544 1.078L41.509 865.69s0 .267-.267.267c-11.321 19.409-17.52 42.051-17.52 64.96 0 70.622 57.416 128.574 127.767 129.382h731.833c70.622-.811 128.036-58.76 128.036-129.382 0-22.909-6.199-45.551-17.787-65.23zM882.5 974.05H152.564c-23.453-.267-42.318-19.676-42.318-43.129 0-7.544 1.889-14.821 5.66-21.298l364.968-609.448.267-.267c8.088-12.665 21.564-20.22 36.661-20.22s28.841 7.821 36.929 20.753L919.159 909.35c3.777 6.466 5.66 14.021 5.66 21.564.267 23.453-18.865 42.86-42.318 43.129zM517.53 456.516c-23.72 0-43.129 19.409-43.129 43.129v172.51c0 23.72 19.409 43.129 43.129 43.129s43.129-19.409 43.129-43.129v-172.51c0-23.72-19.409-43.129-43.129-43.129zm0 345.023c-11.321 0-22.375 4.577-30.461 12.665s-12.665 19.142-12.665 30.461 4.577 22.375 12.665 30.461c8.088 8.088 19.142 12.665 30.461 12.665s22.375-4.577 30.461-12.665c8.088-8.088 12.665-19.142 12.665-30.461s-4.577-22.375-12.665-30.461c-8.088-8.088-19.142-12.665-30.461-12.665z"
                    fill="#f04438"
                  ></path>
                </svg> </span
              >{{ getError('tel') }}
            </p>
          </div>
        </div>
        <div class="formSet">
          <div class="title">
            メールアドレス
            <div class="md_label md_label--must">必須</div>
          </div>
          <div class="form">
            <input
              type="email"
              name="login_id"
              placeholder="メールアドレスを入力"
              class="form--line form--w100"
              :class="{ md_error: errorHas('login_id') || listIdValidation.length }"
              v-model="formParams.login_id"
              @change="checkLoginId"
            />
            <p
              class="errorMessage"
              v-if="listIdValidation.length"
            >
              <span class="iconImgPart">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 150 1100 1100"
                >
                  <path
                    d="m993.554 865.69-.267-.267-364.968-609.718c0-.267-.267-.267-.267-.544-23.986-38.818-65.23-61.994-110.515-61.994s-86.524 23.182-110.246 61.727c-.267.267-.544.544-.544 1.078L41.509 865.69s0 .267-.267.267c-11.321 19.409-17.52 42.051-17.52 64.96 0 70.622 57.416 128.574 127.767 129.382h731.833c70.622-.811 128.036-58.76 128.036-129.382 0-22.909-6.199-45.551-17.787-65.23zM882.5 974.05H152.564c-23.453-.267-42.318-19.676-42.318-43.129 0-7.544 1.889-14.821 5.66-21.298l364.968-609.448.267-.267c8.088-12.665 21.564-20.22 36.661-20.22s28.841 7.821 36.929 20.753L919.159 909.35c3.777 6.466 5.66 14.021 5.66 21.564.267 23.453-18.865 42.86-42.318 43.129zM517.53 456.516c-23.72 0-43.129 19.409-43.129 43.129v172.51c0 23.72 19.409 43.129 43.129 43.129s43.129-19.409 43.129-43.129v-172.51c0-23.72-19.409-43.129-43.129-43.129zm0 345.023c-11.321 0-22.375 4.577-30.461 12.665s-12.665 19.142-12.665 30.461 4.577 22.375 12.665 30.461c8.088 8.088 19.142 12.665 30.461 12.665s22.375-4.577 30.461-12.665c8.088-8.088 12.665-19.142 12.665-30.461s-4.577-22.375-12.665-30.461c-8.088-8.088-19.142-12.665-30.461-12.665z"
                    fill="#f04438"
                  ></path>
                </svg> </span
              >{{ listIdValidation[0] }}
            </p>
            <p
              class="errorMessage"
              v-if="errorHas('login_id')"
            >
              <span class="iconImgPart">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 150 1100 1100"
                >
                  <path
                    d="m993.554 865.69-.267-.267-364.968-609.718c0-.267-.267-.267-.267-.544-23.986-38.818-65.23-61.994-110.515-61.994s-86.524 23.182-110.246 61.727c-.267.267-.544.544-.544 1.078L41.509 865.69s0 .267-.267.267c-11.321 19.409-17.52 42.051-17.52 64.96 0 70.622 57.416 128.574 127.767 129.382h731.833c70.622-.811 128.036-58.76 128.036-129.382 0-22.909-6.199-45.551-17.787-65.23zM882.5 974.05H152.564c-23.453-.267-42.318-19.676-42.318-43.129 0-7.544 1.889-14.821 5.66-21.298l364.968-609.448.267-.267c8.088-12.665 21.564-20.22 36.661-20.22s28.841 7.821 36.929 20.753L919.159 909.35c3.777 6.466 5.66 14.021 5.66 21.564.267 23.453-18.865 42.86-42.318 43.129zM517.53 456.516c-23.72 0-43.129 19.409-43.129 43.129v172.51c0 23.72 19.409 43.129 43.129 43.129s43.129-19.409 43.129-43.129v-172.51c0-23.72-19.409-43.129-43.129-43.129zm0 345.023c-11.321 0-22.375 4.577-30.461 12.665s-12.665 19.142-12.665 30.461 4.577 22.375 12.665 30.461c8.088 8.088 19.142 12.665 30.461 12.665s22.375-4.577 30.461-12.665c8.088-8.088 12.665-19.142 12.665-30.461s-4.577-22.375-12.665-30.461c-8.088-8.088-19.142-12.665-30.461-12.665z"
                    fill="#f04438"
                  ></path>
                </svg> </span
              >{{ getError('login_id') }}
            </p>
          </div>
        </div>
        <div class="formSet">
          <div class="title">
            パスワード
            <div class="md_label md_label--must">必須</div>
          </div>
          <div class="form">
            <input
              type="password"
              name="login_password"
              placeholder="パスワードを入力"
              class="form--line form--w100"
              :class="{ md_error: errorHas('login_password') || !listPwValidation.every(_ => _) }"
              v-model="formParams.login_password"
              @change="checkLoginPw"
            />
            <p
              class="errorMessage"
              v-if="errorHas('login_password')"
            >
              <span class="iconImgPart">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 150 1100 1100"
                >
                  <path
                    d="m993.554 865.69-.267-.267-364.968-609.718c0-.267-.267-.267-.267-.544-23.986-38.818-65.23-61.994-110.515-61.994s-86.524 23.182-110.246 61.727c-.267.267-.544.544-.544 1.078L41.509 865.69s0 .267-.267.267c-11.321 19.409-17.52 42.051-17.52 64.96 0 70.622 57.416 128.574 127.767 129.382h731.833c70.622-.811 128.036-58.76 128.036-129.382 0-22.909-6.199-45.551-17.787-65.23zM882.5 974.05H152.564c-23.453-.267-42.318-19.676-42.318-43.129 0-7.544 1.889-14.821 5.66-21.298l364.968-609.448.267-.267c8.088-12.665 21.564-20.22 36.661-20.22s28.841 7.821 36.929 20.753L919.159 909.35c3.777 6.466 5.66 14.021 5.66 21.564.267 23.453-18.865 42.86-42.318 43.129zM517.53 456.516c-23.72 0-43.129 19.409-43.129 43.129v172.51c0 23.72 19.409 43.129 43.129 43.129s43.129-19.409 43.129-43.129v-172.51c0-23.72-19.409-43.129-43.129-43.129zm0 345.023c-11.321 0-22.375 4.577-30.461 12.665s-12.665 19.142-12.665 30.461 4.577 22.375 12.665 30.461c8.088 8.088 19.142 12.665 30.461 12.665s22.375-4.577 30.461-12.665c8.088-8.088 12.665-19.142 12.665-30.461s-4.577-22.375-12.665-30.461c-8.088-8.088-19.142-12.665-30.461-12.665z"
                    fill="#f04438"
                  ></path>
                </svg> </span
              >{{ getError('login_password') }}
            </p>
            <template v-if="listPwValidation.length == 0">
              <ul class="md_pwRule">
                <li
                  class="rule"
                  id="rule-01"
                >
                  <p class="text">10～20文字で入力</p>
                </li>
                <li
                  class="rule"
                  id="rule-01"
                >
                  <p class="text">英小文字、英大文字、数字、記号から3種類以上を組み合わせて使用</p>
                </li>
                <li
                  class="rule"
                  id="rule-01"
                >
                  <p class="text">異なる文字4つ以上を組み合わせる</p>
                </li>
                <li
                  class="rule"
                  id="rule-01"
                >
                  <p class="text">使用できる記号　@./_-?!#$&amp;*=~</p>
                </li>
                <li
                  class="rule"
                  id="rule-01"
                >
                  <p class="text">3文字連続で同じ文字を使用しない</p>
                </li>
              </ul>
            </template>
            <template v-else-if="listPwValidation.length > 0">
              <ul class="md_pwRule">
                <li
                  v-if="!listPwValidation[0]"
                  class="rule error"
                  id="rule-01"
                >
                  <span
                    class="icon error"
                    style=""
                    ><span class="iconImgPart">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 150 1100 1100"
                      >
                        <path
                          d="m993.554 865.69-.267-.267-364.968-609.718c0-.267-.267-.267-.267-.544-23.986-38.818-65.23-61.994-110.515-61.994s-86.524 23.182-110.246 61.727c-.267.267-.544.544-.544 1.078L41.509 865.69s0 .267-.267.267c-11.321 19.409-17.52 42.051-17.52 64.96 0 70.622 57.416 128.574 127.767 129.382h731.833c70.622-.811 128.036-58.76 128.036-129.382 0-22.909-6.199-45.551-17.787-65.23zM882.5 974.05H152.564c-23.453-.267-42.318-19.676-42.318-43.129 0-7.544 1.889-14.821 5.66-21.298l364.968-609.448.267-.267c8.088-12.665 21.564-20.22 36.661-20.22s28.841 7.821 36.929 20.753L919.159 909.35c3.777 6.466 5.66 14.021 5.66 21.564.267 23.453-18.865 42.86-42.318 43.129zM517.53 456.516c-23.72 0-43.129 19.409-43.129 43.129v172.51c0 23.72 19.409 43.129 43.129 43.129s43.129-19.409 43.129-43.129v-172.51c0-23.72-19.409-43.129-43.129-43.129zm0 345.023c-11.321 0-22.375 4.577-30.461 12.665s-12.665 19.142-12.665 30.461 4.577 22.375 12.665 30.461c8.088 8.088 19.142 12.665 30.461 12.665s22.375-4.577 30.461-12.665c8.088-8.088 12.665-19.142 12.665-30.461s-4.577-22.375-12.665-30.461c-8.088-8.088-19.142-12.665-30.461-12.665z"
                          fill="#f04438"
                        ></path>
                      </svg> </span></span
                  ><span
                    class="icon check"
                    style="display: none"
                    ><span class="iconImgPart">
                      <svg
                        width="9"
                        height="7"
                        viewBox="0 0 9 7"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M8.68689 0.646447C8.88215 0.841709 8.88215 1.15829 8.68689 1.35355L3.64522 6.39522C3.44996 6.59048 3.13338 6.59048 2.93811 6.39522L0.646447 4.10355C0.451184 3.90829 0.451184 3.59171 0.646447 3.39645C0.841709 3.20118 1.15829 3.20118 1.35355 3.39645L3.29167 5.33456L7.97978 0.646447C8.17504 0.451184 8.49162 0.451184 8.68689 0.646447Z"
                          fill="#47CB22"
                        ></path>
                      </svg> </span
                  ></span>
                  <p class="text">10～20文字で入力</p>
                </li>
                <li
                  v-else-if="listPwValidation[0]"
                  class="rule"
                  id="rule-01"
                >
                  <span
                    class="icon error"
                    style="display: none"
                    ><span class="iconImgPart">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 150 1100 1100"
                      >
                        <path
                          d="m993.554 865.69-.267-.267-364.968-609.718c0-.267-.267-.267-.267-.544-23.986-38.818-65.23-61.994-110.515-61.994s-86.524 23.182-110.246 61.727c-.267.267-.544.544-.544 1.078L41.509 865.69s0 .267-.267.267c-11.321 19.409-17.52 42.051-17.52 64.96 0 70.622 57.416 128.574 127.767 129.382h731.833c70.622-.811 128.036-58.76 128.036-129.382 0-22.909-6.199-45.551-17.787-65.23zM882.5 974.05H152.564c-23.453-.267-42.318-19.676-42.318-43.129 0-7.544 1.889-14.821 5.66-21.298l364.968-609.448.267-.267c8.088-12.665 21.564-20.22 36.661-20.22s28.841 7.821 36.929 20.753L919.159 909.35c3.777 6.466 5.66 14.021 5.66 21.564.267 23.453-18.865 42.86-42.318 43.129zM517.53 456.516c-23.72 0-43.129 19.409-43.129 43.129v172.51c0 23.72 19.409 43.129 43.129 43.129s43.129-19.409 43.129-43.129v-172.51c0-23.72-19.409-43.129-43.129-43.129zm0 345.023c-11.321 0-22.375 4.577-30.461 12.665s-12.665 19.142-12.665 30.461 4.577 22.375 12.665 30.461c8.088 8.088 19.142 12.665 30.461 12.665s22.375-4.577 30.461-12.665c8.088-8.088 12.665-19.142 12.665-30.461s-4.577-22.375-12.665-30.461c-8.088-8.088-19.142-12.665-30.461-12.665z"
                          fill="#f04438"
                        ></path>
                      </svg> </span></span
                  ><span
                    class="icon check"
                    style=""
                    ><span class="iconImgPart">
                      <svg
                        width="9"
                        height="7"
                        viewBox="0 0 9 7"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M8.68689 0.646447C8.88215 0.841709 8.88215 1.15829 8.68689 1.35355L3.64522 6.39522C3.44996 6.59048 3.13338 6.59048 2.93811 6.39522L0.646447 4.10355C0.451184 3.90829 0.451184 3.59171 0.646447 3.39645C0.841709 3.20118 1.15829 3.20118 1.35355 3.39645L3.29167 5.33456L7.97978 0.646447C8.17504 0.451184 8.49162 0.451184 8.68689 0.646447Z"
                          fill="#47CB22"
                        ></path>
                      </svg> </span
                  ></span>
                  <p class="text">10～20文字で入力</p>
                </li>
                <li
                  v-if="!listPwValidation[1]"
                  class="rule error"
                  id="rule-02"
                >
                  <span
                    class="icon error"
                    style=""
                    ><span class="iconImgPart">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 150 1100 1100"
                      >
                        <path
                          d="m993.554 865.69-.267-.267-364.968-609.718c0-.267-.267-.267-.267-.544-23.986-38.818-65.23-61.994-110.515-61.994s-86.524 23.182-110.246 61.727c-.267.267-.544.544-.544 1.078L41.509 865.69s0 .267-.267.267c-11.321 19.409-17.52 42.051-17.52 64.96 0 70.622 57.416 128.574 127.767 129.382h731.833c70.622-.811 128.036-58.76 128.036-129.382 0-22.909-6.199-45.551-17.787-65.23zM882.5 974.05H152.564c-23.453-.267-42.318-19.676-42.318-43.129 0-7.544 1.889-14.821 5.66-21.298l364.968-609.448.267-.267c8.088-12.665 21.564-20.22 36.661-20.22s28.841 7.821 36.929 20.753L919.159 909.35c3.777 6.466 5.66 14.021 5.66 21.564.267 23.453-18.865 42.86-42.318 43.129zM517.53 456.516c-23.72 0-43.129 19.409-43.129 43.129v172.51c0 23.72 19.409 43.129 43.129 43.129s43.129-19.409 43.129-43.129v-172.51c0-23.72-19.409-43.129-43.129-43.129zm0 345.023c-11.321 0-22.375 4.577-30.461 12.665s-12.665 19.142-12.665 30.461 4.577 22.375 12.665 30.461c8.088 8.088 19.142 12.665 30.461 12.665s22.375-4.577 30.461-12.665c8.088-8.088 12.665-19.142 12.665-30.461s-4.577-22.375-12.665-30.461c-8.088-8.088-19.142-12.665-30.461-12.665z"
                          fill="#f04438"
                        ></path>
                      </svg> </span></span
                  ><span
                    class="icon check"
                    style="display: none"
                    ><span class="iconImgPart">
                      <svg
                        width="9"
                        height="7"
                        viewBox="0 0 9 7"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M8.68689 0.646447C8.88215 0.841709 8.88215 1.15829 8.68689 1.35355L3.64522 6.39522C3.44996 6.59048 3.13338 6.59048 2.93811 6.39522L0.646447 4.10355C0.451184 3.90829 0.451184 3.59171 0.646447 3.39645C0.841709 3.20118 1.15829 3.20118 1.35355 3.39645L3.29167 5.33456L7.97978 0.646447C8.17504 0.451184 8.49162 0.451184 8.68689 0.646447Z"
                          fill="#47CB22"
                        ></path>
                      </svg> </span
                  ></span>
                  <p class="text">英小文字、英大文字、数字、記号から3種類以上を組み合わせて使用</p>
                </li>
                <li
                  v-else-if="listPwValidation[1]"
                  class="rule"
                  id="rule-02"
                >
                  <span
                    class="icon error"
                    style="display: none"
                    ><span class="iconImgPart">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 150 1100 1100"
                      >
                        <path
                          d="m993.554 865.69-.267-.267-364.968-609.718c0-.267-.267-.267-.267-.544-23.986-38.818-65.23-61.994-110.515-61.994s-86.524 23.182-110.246 61.727c-.267.267-.544.544-.544 1.078L41.509 865.69s0 .267-.267.267c-11.321 19.409-17.52 42.051-17.52 64.96 0 70.622 57.416 128.574 127.767 129.382h731.833c70.622-.811 128.036-58.76 128.036-129.382 0-22.909-6.199-45.551-17.787-65.23zM882.5 974.05H152.564c-23.453-.267-42.318-19.676-42.318-43.129 0-7.544 1.889-14.821 5.66-21.298l364.968-609.448.267-.267c8.088-12.665 21.564-20.22 36.661-20.22s28.841 7.821 36.929 20.753L919.159 909.35c3.777 6.466 5.66 14.021 5.66 21.564.267 23.453-18.865 42.86-42.318 43.129zM517.53 456.516c-23.72 0-43.129 19.409-43.129 43.129v172.51c0 23.72 19.409 43.129 43.129 43.129s43.129-19.409 43.129-43.129v-172.51c0-23.72-19.409-43.129-43.129-43.129zm0 345.023c-11.321 0-22.375 4.577-30.461 12.665s-12.665 19.142-12.665 30.461 4.577 22.375 12.665 30.461c8.088 8.088 19.142 12.665 30.461 12.665s22.375-4.577 30.461-12.665c8.088-8.088 12.665-19.142 12.665-30.461s-4.577-22.375-12.665-30.461c-8.088-8.088-19.142-12.665-30.461-12.665z"
                          fill="#f04438"
                        ></path>
                      </svg> </span></span
                  ><span
                    class="icon check"
                    style=""
                    ><span class="iconImgPart">
                      <svg
                        width="9"
                        height="7"
                        viewBox="0 0 9 7"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M8.68689 0.646447C8.88215 0.841709 8.88215 1.15829 8.68689 1.35355L3.64522 6.39522C3.44996 6.59048 3.13338 6.59048 2.93811 6.39522L0.646447 4.10355C0.451184 3.90829 0.451184 3.59171 0.646447 3.39645C0.841709 3.20118 1.15829 3.20118 1.35355 3.39645L3.29167 5.33456L7.97978 0.646447C8.17504 0.451184 8.49162 0.451184 8.68689 0.646447Z"
                          fill="#47CB22"
                        ></path>
                      </svg> </span
                  ></span>
                  <p class="text">英小文字、英大文字、数字、記号から3種類以上を組み合わせて使用</p>
                </li>
                <li
                  v-if="!listPwValidation[2]"
                  class="rule error"
                  id="rule-03"
                >
                  <span
                    class="icon error"
                    style=""
                    ><span class="iconImgPart">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 150 1100 1100"
                      >
                        <path
                          d="m993.554 865.69-.267-.267-364.968-609.718c0-.267-.267-.267-.267-.544-23.986-38.818-65.23-61.994-110.515-61.994s-86.524 23.182-110.246 61.727c-.267.267-.544.544-.544 1.078L41.509 865.69s0 .267-.267.267c-11.321 19.409-17.52 42.051-17.52 64.96 0 70.622 57.416 128.574 127.767 129.382h731.833c70.622-.811 128.036-58.76 128.036-129.382 0-22.909-6.199-45.551-17.787-65.23zM882.5 974.05H152.564c-23.453-.267-42.318-19.676-42.318-43.129 0-7.544 1.889-14.821 5.66-21.298l364.968-609.448.267-.267c8.088-12.665 21.564-20.22 36.661-20.22s28.841 7.821 36.929 20.753L919.159 909.35c3.777 6.466 5.66 14.021 5.66 21.564.267 23.453-18.865 42.86-42.318 43.129zM517.53 456.516c-23.72 0-43.129 19.409-43.129 43.129v172.51c0 23.72 19.409 43.129 43.129 43.129s43.129-19.409 43.129-43.129v-172.51c0-23.72-19.409-43.129-43.129-43.129zm0 345.023c-11.321 0-22.375 4.577-30.461 12.665s-12.665 19.142-12.665 30.461 4.577 22.375 12.665 30.461c8.088 8.088 19.142 12.665 30.461 12.665s22.375-4.577 30.461-12.665c8.088-8.088 12.665-19.142 12.665-30.461s-4.577-22.375-12.665-30.461c-8.088-8.088-19.142-12.665-30.461-12.665z"
                          fill="#f04438"
                        ></path>
                      </svg> </span></span
                  ><span
                    class="icon check"
                    style="display: none"
                    ><span class="iconImgPart">
                      <svg
                        width="9"
                        height="7"
                        viewBox="0 0 9 7"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M8.68689 0.646447C8.88215 0.841709 8.88215 1.15829 8.68689 1.35355L3.64522 6.39522C3.44996 6.59048 3.13338 6.59048 2.93811 6.39522L0.646447 4.10355C0.451184 3.90829 0.451184 3.59171 0.646447 3.39645C0.841709 3.20118 1.15829 3.20118 1.35355 3.39645L3.29167 5.33456L7.97978 0.646447C8.17504 0.451184 8.49162 0.451184 8.68689 0.646447Z"
                          fill="#47CB22"
                        ></path>
                      </svg> </span
                  ></span>
                  <p class="text">異なる文字4つ以上を組み合わせる</p>
                </li>
                <li
                  v-else-if="listPwValidation[2]"
                  class="rule"
                  id="rule-03"
                >
                  <span
                    class="icon error"
                    style="display: none"
                    ><span class="iconImgPart">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 150 1100 1100"
                      >
                        <path
                          d="m993.554 865.69-.267-.267-364.968-609.718c0-.267-.267-.267-.267-.544-23.986-38.818-65.23-61.994-110.515-61.994s-86.524 23.182-110.246 61.727c-.267.267-.544.544-.544 1.078L41.509 865.69s0 .267-.267.267c-11.321 19.409-17.52 42.051-17.52 64.96 0 70.622 57.416 128.574 127.767 129.382h731.833c70.622-.811 128.036-58.76 128.036-129.382 0-22.909-6.199-45.551-17.787-65.23zM882.5 974.05H152.564c-23.453-.267-42.318-19.676-42.318-43.129 0-7.544 1.889-14.821 5.66-21.298l364.968-609.448.267-.267c8.088-12.665 21.564-20.22 36.661-20.22s28.841 7.821 36.929 20.753L919.159 909.35c3.777 6.466 5.66 14.021 5.66 21.564.267 23.453-18.865 42.86-42.318 43.129zM517.53 456.516c-23.72 0-43.129 19.409-43.129 43.129v172.51c0 23.72 19.409 43.129 43.129 43.129s43.129-19.409 43.129-43.129v-172.51c0-23.72-19.409-43.129-43.129-43.129zm0 345.023c-11.321 0-22.375 4.577-30.461 12.665s-12.665 19.142-12.665 30.461 4.577 22.375 12.665 30.461c8.088 8.088 19.142 12.665 30.461 12.665s22.375-4.577 30.461-12.665c8.088-8.088 12.665-19.142 12.665-30.461s-4.577-22.375-12.665-30.461c-8.088-8.088-19.142-12.665-30.461-12.665z"
                          fill="#f04438"
                        ></path>
                      </svg> </span></span
                  ><span
                    class="icon check"
                    style=""
                    ><span class="iconImgPart">
                      <svg
                        width="9"
                        height="7"
                        viewBox="0 0 9 7"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M8.68689 0.646447C8.88215 0.841709 8.88215 1.15829 8.68689 1.35355L3.64522 6.39522C3.44996 6.59048 3.13338 6.59048 2.93811 6.39522L0.646447 4.10355C0.451184 3.90829 0.451184 3.59171 0.646447 3.39645C0.841709 3.20118 1.15829 3.20118 1.35355 3.39645L3.29167 5.33456L7.97978 0.646447C8.17504 0.451184 8.49162 0.451184 8.68689 0.646447Z"
                          fill="#47CB22"
                        ></path>
                      </svg> </span
                  ></span>
                  <p class="text">異なる文字4つ以上を組み合わせる</p>
                </li>
                <li
                  v-if="!listPwValidation[3]"
                  class="rule error"
                  id="rule-04"
                >
                  <span
                    class="icon error"
                    style=""
                    ><span class="iconImgPart">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 150 1100 1100"
                      >
                        <path
                          d="m993.554 865.69-.267-.267-364.968-609.718c0-.267-.267-.267-.267-.544-23.986-38.818-65.23-61.994-110.515-61.994s-86.524 23.182-110.246 61.727c-.267.267-.544.544-.544 1.078L41.509 865.69s0 .267-.267.267c-11.321 19.409-17.52 42.051-17.52 64.96 0 70.622 57.416 128.574 127.767 129.382h731.833c70.622-.811 128.036-58.76 128.036-129.382 0-22.909-6.199-45.551-17.787-65.23zM882.5 974.05H152.564c-23.453-.267-42.318-19.676-42.318-43.129 0-7.544 1.889-14.821 5.66-21.298l364.968-609.448.267-.267c8.088-12.665 21.564-20.22 36.661-20.22s28.841 7.821 36.929 20.753L919.159 909.35c3.777 6.466 5.66 14.021 5.66 21.564.267 23.453-18.865 42.86-42.318 43.129zM517.53 456.516c-23.72 0-43.129 19.409-43.129 43.129v172.51c0 23.72 19.409 43.129 43.129 43.129s43.129-19.409 43.129-43.129v-172.51c0-23.72-19.409-43.129-43.129-43.129zm0 345.023c-11.321 0-22.375 4.577-30.461 12.665s-12.665 19.142-12.665 30.461 4.577 22.375 12.665 30.461c8.088 8.088 19.142 12.665 30.461 12.665s22.375-4.577 30.461-12.665c8.088-8.088 12.665-19.142 12.665-30.461s-4.577-22.375-12.665-30.461c-8.088-8.088-19.142-12.665-30.461-12.665z"
                          fill="#f04438"
                        ></path>
                      </svg> </span></span
                  ><span
                    class="icon check"
                    style="display: none"
                    ><span class="iconImgPart">
                      <svg
                        width="9"
                        height="7"
                        viewBox="0 0 9 7"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M8.68689 0.646447C8.88215 0.841709 8.88215 1.15829 8.68689 1.35355L3.64522 6.39522C3.44996 6.59048 3.13338 6.59048 2.93811 6.39522L0.646447 4.10355C0.451184 3.90829 0.451184 3.59171 0.646447 3.39645C0.841709 3.20118 1.15829 3.20118 1.35355 3.39645L3.29167 5.33456L7.97978 0.646447C8.17504 0.451184 8.49162 0.451184 8.68689 0.646447Z"
                          fill="#47CB22"
                        ></path>
                      </svg> </span
                  ></span>
                  <p class="text">使用できる記号　@./_-?!#$&amp;*=~</p>
                </li>
                <li
                  v-else-if="listPwValidation[3]"
                  class="rule"
                  id="rule-04"
                >
                  <span
                    class="icon error"
                    style="display: none"
                    ><span class="iconImgPart">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 150 1100 1100"
                      >
                        <path
                          d="m993.554 865.69-.267-.267-364.968-609.718c0-.267-.267-.267-.267-.544-23.986-38.818-65.23-61.994-110.515-61.994s-86.524 23.182-110.246 61.727c-.267.267-.544.544-.544 1.078L41.509 865.69s0 .267-.267.267c-11.321 19.409-17.52 42.051-17.52 64.96 0 70.622 57.416 128.574 127.767 129.382h731.833c70.622-.811 128.036-58.76 128.036-129.382 0-22.909-6.199-45.551-17.787-65.23zM882.5 974.05H152.564c-23.453-.267-42.318-19.676-42.318-43.129 0-7.544 1.889-14.821 5.66-21.298l364.968-609.448.267-.267c8.088-12.665 21.564-20.22 36.661-20.22s28.841 7.821 36.929 20.753L919.159 909.35c3.777 6.466 5.66 14.021 5.66 21.564.267 23.453-18.865 42.86-42.318 43.129zM517.53 456.516c-23.72 0-43.129 19.409-43.129 43.129v172.51c0 23.72 19.409 43.129 43.129 43.129s43.129-19.409 43.129-43.129v-172.51c0-23.72-19.409-43.129-43.129-43.129zm0 345.023c-11.321 0-22.375 4.577-30.461 12.665s-12.665 19.142-12.665 30.461 4.577 22.375 12.665 30.461c8.088 8.088 19.142 12.665 30.461 12.665s22.375-4.577 30.461-12.665c8.088-8.088 12.665-19.142 12.665-30.461s-4.577-22.375-12.665-30.461c-8.088-8.088-19.142-12.665-30.461-12.665z"
                          fill="#f04438"
                        ></path>
                      </svg> </span></span
                  ><span
                    class="icon check"
                    style=""
                    ><span class="iconImgPart">
                      <svg
                        width="9"
                        height="7"
                        viewBox="0 0 9 7"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M8.68689 0.646447C8.88215 0.841709 8.88215 1.15829 8.68689 1.35355L3.64522 6.39522C3.44996 6.59048 3.13338 6.59048 2.93811 6.39522L0.646447 4.10355C0.451184 3.90829 0.451184 3.59171 0.646447 3.39645C0.841709 3.20118 1.15829 3.20118 1.35355 3.39645L3.29167 5.33456L7.97978 0.646447C8.17504 0.451184 8.49162 0.451184 8.68689 0.646447Z"
                          fill="#47CB22"
                        ></path>
                      </svg> </span
                  ></span>
                  <p class="text">使用できる記号　@./_-?!#$&amp;*=~</p>
                </li>
                <li
                  v-if="!listPwValidation[4]"
                  class="rule error"
                  id="rule-05"
                >
                  <span
                    class="icon error"
                    style=""
                    ><span class="iconImgPart">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 150 1100 1100"
                      >
                        <path
                          d="m993.554 865.69-.267-.267-364.968-609.718c0-.267-.267-.267-.267-.544-23.986-38.818-65.23-61.994-110.515-61.994s-86.524 23.182-110.246 61.727c-.267.267-.544.544-.544 1.078L41.509 865.69s0 .267-.267.267c-11.321 19.409-17.52 42.051-17.52 64.96 0 70.622 57.416 128.574 127.767 129.382h731.833c70.622-.811 128.036-58.76 128.036-129.382 0-22.909-6.199-45.551-17.787-65.23zM882.5 974.05H152.564c-23.453-.267-42.318-19.676-42.318-43.129 0-7.544 1.889-14.821 5.66-21.298l364.968-609.448.267-.267c8.088-12.665 21.564-20.22 36.661-20.22s28.841 7.821 36.929 20.753L919.159 909.35c3.777 6.466 5.66 14.021 5.66 21.564.267 23.453-18.865 42.86-42.318 43.129zM517.53 456.516c-23.72 0-43.129 19.409-43.129 43.129v172.51c0 23.72 19.409 43.129 43.129 43.129s43.129-19.409 43.129-43.129v-172.51c0-23.72-19.409-43.129-43.129-43.129zm0 345.023c-11.321 0-22.375 4.577-30.461 12.665s-12.665 19.142-12.665 30.461 4.577 22.375 12.665 30.461c8.088 8.088 19.142 12.665 30.461 12.665s22.375-4.577 30.461-12.665c8.088-8.088 12.665-19.142 12.665-30.461s-4.577-22.375-12.665-30.461c-8.088-8.088-19.142-12.665-30.461-12.665z"
                          fill="#f04438"
                        ></path>
                      </svg> </span></span
                  ><span
                    class="icon check"
                    style="display: none"
                    ><span class="iconImgPart">
                      <svg
                        width="9"
                        height="7"
                        viewBox="0 0 9 7"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M8.68689 0.646447C8.88215 0.841709 8.88215 1.15829 8.68689 1.35355L3.64522 6.39522C3.44996 6.59048 3.13338 6.59048 2.93811 6.39522L0.646447 4.10355C0.451184 3.90829 0.451184 3.59171 0.646447 3.39645C0.841709 3.20118 1.15829 3.20118 1.35355 3.39645L3.29167 5.33456L7.97978 0.646447C8.17504 0.451184 8.49162 0.451184 8.68689 0.646447Z"
                          fill="#47CB22"
                        ></path>
                      </svg> </span
                  ></span>
                  <p class="text">3文字連続で同じ文字を使用しない</p>
                </li>
                <li
                  v-else-if="listPwValidation[4]"
                  class="rule"
                  id="rule-05"
                >
                  <span
                    class="icon error"
                    style="display: none"
                    ><span class="iconImgPart">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 150 1100 1100"
                      >
                        <path
                          d="m993.554 865.69-.267-.267-364.968-609.718c0-.267-.267-.267-.267-.544-23.986-38.818-65.23-61.994-110.515-61.994s-86.524 23.182-110.246 61.727c-.267.267-.544.544-.544 1.078L41.509 865.69s0 .267-.267.267c-11.321 19.409-17.52 42.051-17.52 64.96 0 70.622 57.416 128.574 127.767 129.382h731.833c70.622-.811 128.036-58.76 128.036-129.382 0-22.909-6.199-45.551-17.787-65.23zM882.5 974.05H152.564c-23.453-.267-42.318-19.676-42.318-43.129 0-7.544 1.889-14.821 5.66-21.298l364.968-609.448.267-.267c8.088-12.665 21.564-20.22 36.661-20.22s28.841 7.821 36.929 20.753L919.159 909.35c3.777 6.466 5.66 14.021 5.66 21.564.267 23.453-18.865 42.86-42.318 43.129zM517.53 456.516c-23.72 0-43.129 19.409-43.129 43.129v172.51c0 23.72 19.409 43.129 43.129 43.129s43.129-19.409 43.129-43.129v-172.51c0-23.72-19.409-43.129-43.129-43.129zm0 345.023c-11.321 0-22.375 4.577-30.461 12.665s-12.665 19.142-12.665 30.461 4.577 22.375 12.665 30.461c8.088 8.088 19.142 12.665 30.461 12.665s22.375-4.577 30.461-12.665c8.088-8.088 12.665-19.142 12.665-30.461s-4.577-22.375-12.665-30.461c-8.088-8.088-19.142-12.665-30.461-12.665z"
                          fill="#f04438"
                        ></path>
                      </svg> </span></span
                  ><span
                    class="icon check"
                    style=""
                    ><span class="iconImgPart">
                      <svg
                        width="9"
                        height="7"
                        viewBox="0 0 9 7"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M8.68689 0.646447C8.88215 0.841709 8.88215 1.15829 8.68689 1.35355L3.64522 6.39522C3.44996 6.59048 3.13338 6.59048 2.93811 6.39522L0.646447 4.10355C0.451184 3.90829 0.451184 3.59171 0.646447 3.39645C0.841709 3.20118 1.15829 3.20118 1.35355 3.39645L3.29167 5.33456L7.97978 0.646447C8.17504 0.451184 8.49162 0.451184 8.68689 0.646447Z"
                          fill="#47CB22"
                        ></path>
                      </svg> </span
                  ></span>
                  <p class="text">3文字連続で同じ文字を使用しない</p>
                </li>
              </ul>
            </template>
          </div>
        </div>
      </div>
      <div class="btnArea">
        <div class="btnSet">
          <RouterLink
            class="md_btn md_btn--submit"
            :class="{ 'md_btn--disable': !submitable }"
            :to="{ name: 'university' }"
            @click="next"
            >次へ進む</RouterLink
          >
        </div>
      </div>
    </div>
  </div>
</template>
